/**
 * Barre de navigation du détail d'une campagne, sur deux niveaux :
 *
 * - les onglets : Candidats (transverse), un onglet par test, et Référentiels
 *   rangé à droite — la préparation des matchings, pas un résultat ;
 * - sous un onglet de test, ses sections, en contrôle segmenté — visiblement
 *   secondaire (pilule, sans soulignement) pour ne pas se lire comme un
 *   second rang d'onglets.
 *
 * Présentationnel : ce qui est visible se décide dans `CampagneDetailView`.
 */
import type { TestCampagne } from "@repo/results";
import { Separator } from "@repo/ui";
import { Compass, Users } from "lucide-react";
import { BoutonOnglet, ControleSegmente } from "./elementsNavigation";
import {
  LIBELLE_SECTION,
  LIBELLE_TEST,
  type OngletCampagne,
  type SectionTest,
} from "./ongletsCampagne";

type Props = {
  tests: readonly TestCampagne[];
  /** La campagne porte-t-elle un référentiel à préparer (PAMI, PC Fit) ? */
  avecReferentiels: boolean;
  onglet: OngletCampagne;
  /** Sections du test actif. Moins de deux : rien à choisir, pas de contrôle. */
  sections: readonly SectionTest[];
  section: SectionTest;
  onChoisirOnglet: (onglet: OngletCampagne) => void;
  onChoisirSection: (section: SectionTest) => void;
};

export function NavigationCampagne({
  tests,
  avecReferentiels,
  onglet,
  sections,
  section,
  onChoisirOnglet,
  onChoisirSection,
}: Props) {
  const bouton = (valeur: OngletCampagne, libelle: string, icone?: React.ReactNode) => (
    <BoutonOnglet
      key={valeur}
      actif={onglet === valeur}
      onClick={() => onChoisirOnglet(valeur)}
      icone={icone}
    >
      {libelle}
    </BoutonOnglet>
  );

  return (
    <div className="mb-6 flex flex-col gap-4">
      <nav
        aria-label="Vues de la campagne"
        className="flex flex-wrap items-center gap-1 border-b"
      >
        {bouton("candidats", "Candidats", <Users className="h-4 w-4" />)}
        {/* Le filet sépare la vue transverse des vues par test. */}
        <Separator orientation="vertical" className="mx-2 hidden h-5 sm:block" />
        {tests.map((test) => bouton(test, LIBELLE_TEST[test]))}
        {avecReferentiels && (
          <div className="ml-auto">
            {bouton("referentiels", "Référentiels", <Compass className="h-4 w-4" />)}
          </div>
        )}
      </nav>

      {sections.length > 1 && (
        <ControleSegmente
          libelle="Sections du test"
          options={sections.map((s) => ({ valeur: s, libelle: LIBELLE_SECTION[s] }))}
          valeur={section}
          onChoisir={onChoisirSection}
        />
      )}
    </div>
  );
}
